import type {
	AddProvidersToOrganizationInput,
	DirectoryOrganization,
	DirectoryProvider,
} from "@repo/api/client";
import {
	Button,
	Checkbox,
	Flex,
	Icon,
	Select,
	Text,
	TextField,
} from "@repo/ui";
import { useState } from "react";
import { toast } from "sonner";
import { useApiClient } from "../../contexts/ApiContext";
import {
	type DirectorySpecialty,
	formatLocation,
	userFacingMessage,
} from "./directoryAdminUtils";

type ProviderEntry = AddProvidersToOrganizationInput["providers"][number];

type SpecialtyPickerProps = {
	specialties: DirectorySpecialty[];
	selectedIds: string[];
	onChange: (ids: string[]) => void;
	/** False for a saved provider: specialties are only added from here. */
	canRemove?: boolean;
	placeholder?: string;
};

function SpecialtyPicker({
	specialties,
	selectedIds,
	onChange,
	canRemove = true,
	placeholder = "Add specialty...",
}: SpecialtyPickerProps) {
	const nameById = new Map(specialties.map((s) => [s.id, s.name]));
	// The Select is uncontrolled (`value={undefined}` leaves react-aria holding
	// its own selection), so it would keep showing the last pick. Remounting
	// after each pick puts the placeholder back.
	const [pickCount, setPickCount] = useState(0);
	return (
		<Flex gap="xs" align="center" wrap>
			{selectedIds.map((id) => (
				<Flex
					key={id}
					gap="xxs"
					align="center"
					paddingHorizontal="sm"
					paddingVertical="xxs"
					background="blue3"
					borderRadius="md"
				>
					<Text size="sm" color="blue11">
						{nameById.get(id) ?? id}
					</Text>
					{canRemove ? (
						<Button
							variant="ghost"
							size="xxs"
							aria-label={`Remove ${nameById.get(id) ?? "specialty"}`}
							onPress={() =>
								onChange(selectedIds.filter((other) => other !== id))
							}
						>
							<Icon name="X" size="xs" color="blue11" />
						</Button>
					) : null}
				</Flex>
			))}
			<Select
				key={pickCount}
				aria-label={placeholder}
				placeholder={placeholder}
				size="xs"
				options={specialties
					.filter((s) => !selectedIds.includes(s.id))
					.map((s) => ({ value: s.id, label: s.name }))}
				value={undefined}
				onSelectionChange={(value) => {
					if (value) {
						onChange([...selectedIds, value]);
						setPickCount((count) => count + 1);
					}
				}}
			/>
		</Flex>
	);
}

type OrganizationProvidersProps = {
	organization: DirectoryOrganization;
	specialties: DirectorySpecialty[];
	onChanged: (organization: DirectoryOrganization) => void;
};

/**
 * The providers linked to an organization, and the form to add more.
 *
 * Agent search only reaches an organization through a provider with a
 * matching specialty, so an organization with none here can't be referred to.
 * For an agency such as home health, add a contact person (or an entry named
 * after the agency, with no NPI) with the agency's specialty.
 */
export function OrganizationProviders({
	organization,
	specialties,
	onChanged,
}: OrganizationProvidersProps) {
	const apiClient = useApiClient();
	const [isSaving, setIsSaving] = useState(false);

	const [mode, setMode] = useState<"new" | "existing">("new");
	const [firstName, setFirstName] = useState("");
	const [middleName, setMiddleName] = useState("");
	const [lastName, setLastName] = useState("");
	const [npi, setNpi] = useState("");
	const [lookupNpi, setLookupNpi] = useState("");
	const [lookedUp, setLookedUp] = useState<
		DirectoryProvider | null | undefined
	>(undefined);
	const [specialtyIds, setSpecialtyIds] = useState<string[]>([]);
	const [offersTelehealth, setOffersTelehealth] = useState(false);
	const [locationIds, setLocationIds] = useState<string[]>(() =>
		organization.locations.length === 1 ? [organization.locations[0]!.id] : [],
	);

	const resetForm = () => {
		setFirstName("");
		setMiddleName("");
		setLastName("");
		setNpi("");
		setLookupNpi("");
		setLookedUp(undefined);
		setSpecialtyIds([]);
		setOffersTelehealth(false);
	};

	const submit = async (entry: ProviderEntry, successMessage: string) => {
		setIsSaving(true);
		try {
			const result = await apiClient.directoryAdmin.addProvidersToOrganization({
				organizationId: organization.id,
				providers: [entry],
			});
			toast.success(successMessage);
			onChanged(result.organization);
			return true;
		} catch (error) {
			console.error("Failed to add provider to organization:", error);
			toast.error(
				userFacingMessage(
					error,
					"We couldn't save this provider. Please try again",
				),
			);
			return false;
		} finally {
			setIsSaving(false);
		}
	};

	const handleLookup = async () => {
		try {
			const result = await apiClient.directoryAdmin.lookupProviderByNpi({
				npi: lookupNpi,
			});
			setLookedUp(result.provider);
			setOffersTelehealth(result.provider?.offersTelehealth ?? false);
		} catch (error) {
			console.error("Failed to look up provider:", error);
			toast.error(
				userFacingMessage(
					error,
					"We couldn't look up that NPI. Please try again",
				),
			);
		}
	};

	const handleAdd = async () => {
		const entry: ProviderEntry =
			mode === "new"
				? {
						kind: "new",
						firstName,
						middleName,
						lastName,
						npi,
						offersTelehealth,
						specialtyIds,
						locationIds,
					}
				: {
						kind: "existing",
						providerId: lookedUp!.id,
						specialtyIds,
						locationIds,
						offersTelehealth,
					};
		if (await submit(entry, "Provider added")) {
			resetForm();
		}
	};

	// Agent search filters by location and faxes a location, so a provider
	// added to an org with locations needs one. The API enforces this too.
	const needsLocation =
		organization.locations.length > 0 && locationIds.length === 0;
	const canAdd =
		!isSaving &&
		!needsLocation &&
		(mode === "new"
			? firstName.trim() !== "" &&
				lastName.trim() !== "" &&
				specialtyIds.length > 0
			: lookedUp?.editable === true);

	return (
		<Flex column gap="lg">
			<Text size="md" weight="semibold" color="sand12">
				Providers ({organization.providers.length})
			</Text>

			{organization.providers.length === 0 ? (
				<Text size="sm" color="sand11">
					No providers yet. Referrals can't be routed to this organization until
					it has a provider with a specialty.
				</Text>
			) : (
				<Flex column gap="sm">
					{organization.providers.map((provider) => (
						<Flex
							key={provider.id}
							column
							gap="xs"
							padding="md"
							background="sand2"
							borderRadius="md"
						>
							<Flex gap="sm" align="center">
								<Icon name="Stethoscope" size="sm" color="sand11" />
								<Text size="sm" weight="medium" color="sand12">
									{provider.name}
								</Text>
								<Text size="sm" color="sand10">
									{provider.npi ? `NPI ${provider.npi}` : "No NPI"}
									{provider.source ? ` · ${provider.source}` : ""}
								</Text>
							</Flex>
							{provider.editable ? (
								<SpecialtyPicker
									specialties={specialties}
									selectedIds={provider.specialties.map((s) => s.id)}
									canRemove={false}
									onChange={(ids) => {
										const added = ids.filter(
											(id) => !provider.specialties.some((s) => s.id === id),
										);
										// Specialties are only added from here. Removing one
										// would change how the agent routes to a provider other
										// practices share.
										if (added.length > 0) {
											void submit(
												{
													kind: "existing",
													providerId: provider.id,
													specialtyIds: added,
													locationIds: [],
												},
												"Specialty added",
											);
										}
									}}
								/>
							) : (
								<Text size="sm" color="sand11">
									{provider.specialties.map((s) => s.name).join(", ") ||
										"No specialty"}
								</Text>
							)}
							<Checkbox
								size="sm"
								label="Offers telehealth"
								isSelected={provider.offersTelehealth}
								isDisabled={!provider.editable || isSaving}
								onChange={(isSelected) =>
									void submit(
										{
											kind: "existing",
											providerId: provider.id,
											specialtyIds: [],
											locationIds: [],
											offersTelehealth: isSelected,
										},
										isSelected
											? "Marked as offering telehealth"
											: "Marked as not offering telehealth",
									)
								}
							/>
						</Flex>
					))}
				</Flex>
			)}

			{organization.editable ? (
				<Flex
					column
					gap="md"
					padding="md"
					borderWidth="md"
					borderColor="sandA5"
					borderRadius="md"
				>
					<Flex gap="sm" align="center">
						<Text size="sm" weight="semibold" color="sand12">
							Add a provider
						</Text>
						<Button
							size="xs"
							variant={mode === "new" ? "primary" : "outline"}
							onPress={() => setMode("new")}
						>
							New provider
						</Button>
						<Button
							size="xs"
							variant={mode === "existing" ? "primary" : "outline"}
							onPress={() => setMode("existing")}
						>
							Existing provider
						</Button>
					</Flex>

					{mode === "new" ? (
						<Flex column gap="sm">
							<Flex gap="sm">
								<Flex column flex>
									<TextField
										label="First name"
										value={firstName}
										onChange={(e) => setFirstName(e.target.value)}
									/>
								</Flex>
								<Flex column flex>
									<TextField
										label="Middle (optional)"
										value={middleName}
										onChange={(e) => setMiddleName(e.target.value)}
									/>
								</Flex>
							</Flex>
							<TextField
								label="Last name (no credentials such as MD or NP)"
								value={lastName}
								onChange={(e) => setLastName(e.target.value)}
							/>
							<TextField
								label="Provider NPI (optional)"
								value={npi}
								onChange={(e) => setNpi(e.target.value)}
							/>
						</Flex>
					) : (
						<Flex column gap="sm">
							<Flex gap="sm" align="flex-end">
								<Flex column flex>
									<TextField
										label="Provider NPI"
										value={lookupNpi}
										onChange={(e) => {
											setLookupNpi(e.target.value);
											setLookedUp(undefined);
										}}
									/>
								</Flex>
								<Button
									variant="outline"
									size="sm"
									isDisabled={lookupNpi.trim() === ""}
									onPress={handleLookup}
								>
									Look up
								</Button>
							</Flex>
							{lookedUp === null ? (
								<Text size="sm" color="sand11">
									No provider in the directory has this NPI. Choose "New
									provider" to add them.
								</Text>
							) : lookedUp ? (
								<Text size="sm" color={lookedUp.editable ? "sand12" : "red11"}>
									{lookedUp.editable
										? `${lookedUp.name}${
												lookedUp.specialties.length > 0
													? ` (${lookedUp.specialties.map((s) => s.name).join(", ")})`
													: ""
											}`
										: `${lookedUp.name} belongs to another practice, so they can't be added from here.`}
								</Text>
							) : null}
						</Flex>
					)}

					<Flex column gap="xs">
						<Text size="sm" weight="medium" color="sand11">
							{mode === "new" ? "Specialties" : "Specialties to add"}
						</Text>
						<SpecialtyPicker
							specialties={specialties}
							selectedIds={specialtyIds}
							onChange={setSpecialtyIds}
						/>
					</Flex>

					<Flex column gap="xs">
						<Text size="sm" weight="medium" color="sand11">
							Sees patients at
						</Text>
						{organization.locations.length === 0 ? (
							<Text size="sm" color="amber11">
								Add a location to this organization first, so referrals have a
								fax number to go to.
							</Text>
						) : (
							organization.locations.map((location) => (
								<Checkbox
									key={location.id}
									size="sm"
									label={formatLocation(location)}
									isSelected={locationIds.includes(location.id)}
									onChange={(isSelected) =>
										setLocationIds((current) =>
											isSelected
												? [...current, location.id]
												: current.filter((id) => id !== location.id),
										)
									}
								/>
							))
						)}
						{needsLocation ? (
							<Text size="sm" color="sand11">
								Choose at least one, so referrals have a fax number to go to.
							</Text>
						) : null}
					</Flex>

					<Checkbox
						size="sm"
						label="Offers telehealth"
						isSelected={offersTelehealth}
						onChange={setOffersTelehealth}
					/>

					<Button alignSelf="start" isDisabled={!canAdd} onPress={handleAdd}>
						{isSaving ? "Saving..." : "Add provider"}
					</Button>
				</Flex>
			) : null}
		</Flex>
	);
}
